import React, { useCallback, useMemo, useState } from 'react';
import { Card, Spinner } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import { useInifinityScroll } from '../../../../../components/loadingMoreItemsSpinner/useInfinityScroll';
import {
  ManagerReportsType,
  useEmployeesObjectiveAchievementsQuery,
} from '../../../__generated__/graphql';
import { GoalId } from '../Goals2/types';
import { GoalSidebar } from '../Goals2/GoalSidebar/GoalSidebar';
import {
  GoalsContextProvider,
  useGoalsContext,
} from '../Goals2/GoalsContext/GoalsContext';
import { EmployeeAchievementCard } from './EmployeeAchievementCard';
import { canEditGoalResult } from '../hooks/useCanEditGoalResult';

const PAGE_SIZE = 20;

const LoadingOverlay: React.FC = () => (
  <div
    style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(var(--bs-body-bg-rgb), 0.6)',
      zIndex: 1,
    }}>
    <Spinner animation="border" />
  </div>
);

type Props = {
  objectiveTimePeriodId: string;
};

export const EmployeesObjectiveAchievement: React.FC<Props> = ({
  objectiveTimePeriodId,
}) => (
  <GoalsContextProvider isManagerView>
    <EmployeesObjectiveAchievementContent
      objectiveTimePeriodId={objectiveTimePeriodId}
    />
  </GoalsContextProvider>
);

const EmployeesObjectiveAchievementContent: React.FC<Props> = ({
  objectiveTimePeriodId,
}) => {
  const { objectiveResultUpdateMode } = useGoalsContext();
  const { data, loading, refetch, fetchMore } =
    useEmployeesObjectiveAchievementsQuery({
      variables: {
        objectiveTimePeriodId,
        filter: {
          managerReportsType: ManagerReportsType.Direct,
          employeeId: null,
        },
        offset: 0,
        limit: PAGE_SIZE,
        skipPaginationInfo: false,
      },
      fetchPolicy: 'cache-and-network',
    });

  const [selectedGoalId, setSelectedGoalId] = useState<GoalId | null>(null);

  const handleGoalClick = useCallback((objectiveId: string) => {
    setSelectedGoalId({ id: objectiveId, type: 'employee' });
  }, []);

  const handleSidebarClose = useCallback(() => {
    setSelectedGoalId(null);
  }, []);

  const [isUpdatingResult, setIsUpdatingResult] = useState(false);

  const handleResultUpdated = async () => {
    setIsUpdatingResult(true);
    try {
      await refetch();
    } finally {
      setIsUpdatingResult(false);
    }
  };

  const paginationData = useMemo(
    () => data?.manager.employeesObjectiveAchievements,
    [data],
  );

  const { LoadingMore } = useInifinityScroll({
    objectWithPaginationInfo: paginationData,
    fetchMore: (offset) => {
      return fetchMore({
        variables: {
          offset,
          skipPaginationInfo: true,
        },
      }).then();
    },
  });

  if (loading && !data) {
    return (
      <>
        {[0, 1, 2].map((i) => (
          <Card className="mb-3" key={i}>
            <Card.Header className="border-bottom-0">
              <div className="d-flex justify-content-between w-100">
                <div className="d-flex align-items-center gap-2">
                  <Skeleton circle width={28} height={28} />
                  <div>
                    <Skeleton width={120} height={14} />
                  </div>
                </div>
                <Skeleton width={100} height={12} />
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <Skeleton count={3} height={36} className="mb-1 mx-3" />
            </Card.Body>
          </Card>
        ))}
      </>
    );
  }

  const achievements = data?.manager.employeesObjectiveAchievements.items ?? [];
  const canEditResults = canEditGoalResult({
    mode: objectiveResultUpdateMode,
    isManager: true,
    isEmployee: false,
  });

  return (
    <>
      <div style={{ position: 'relative' }}>
        {isUpdatingResult && <LoadingOverlay />}
        {achievements.map((achievement) => (
          <EmployeeAchievementCard
            key={achievement.employeeId}
            achievement={achievement}
            canEditResults={canEditResults}
            onResultUpdated={handleResultUpdated}
            onGoalClick={handleGoalClick}
          />
        ))}
      </div>
      {LoadingMore}
      <GoalSidebar
        visible={selectedGoalId !== null}
        goalId={selectedGoalId}
        onClose={handleSidebarClose}
      />
    </>
  );
};
